import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Laptop, RotateCcw, AlertTriangle, Plus, CheckCircle2, XCircle, Hourglass, Search, X, Calendar, Infinity as InfinityIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const STATUS_STYLES = {
  available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  assigned:  'bg-blue-50    text-blue-700    border-blue-200',
  repair:    'bg-amber-50   text-amber-700   border-amber-200',
  lost:      'bg-rose-50    text-rose-700    border-rose-200',
  damaged:   'bg-rose-50    text-rose-700    border-rose-200',
  retired:   'bg-slate-100  text-slate-600   border-slate-200',
  disposed:  'bg-slate-100  text-slate-600   border-slate-200',
};

const ACK_STYLES = {
  pending:      { cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Hourglass, label: 'Awaiting acknowledgement' },
  acknowledged: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2, label: 'Acknowledged' },
};

/**
 * Lists assets currently or previously assigned to an employee.
 * - Self-view: read-only, but pending-ack assets get "Acknowledge / Not received" buttons.
 * - HR/Admin/IT view: + Assign button, Return button.
 */
export default function EmployeeAssetsSection({ employeeId, employeeName, canManage = false, terminated = false }) {
  const { user } = useAuth();
  const isSelf = user?.id === employeeId;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returnFor, setReturnFor] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/assets/by-employee/${employeeId}`);
        if (cancelled) return;
        setRows(data || []);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId, bump]);

  const outstanding = rows.filter(r => r.status === 'assigned');
  const pendingAck = rows.filter(r => r.status === 'assigned' && r.current_assignment?.acknowledgement_status === 'pending');

  const onAcknowledge = async (asset) => {
    try {
      await api.post(`/assets/${asset.id}/acknowledge`);
      toast.success('Receipt acknowledged');
      setBump(b => b + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not acknowledge');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="employee-assets-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Company assets</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">
            {isSelf ? 'Assigned to you' : 'Assigned to this employee'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            <b className="text-slate-900">{outstanding.length}</b> outstanding · <b className="text-slate-900">{rows.length}</b> total
          </span>
          {canManage && !terminated && (
            <Button onClick={() => setAssignOpen(true)} size="sm" className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="assign-asset-to-employee-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Assign asset
            </Button>
          )}
        </div>
      </div>

      {/* Self-view nudge for pending acknowledgement */}
      {isSelf && pendingAck.length > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900" data-testid="pending-ack-banner">
          <div className="flex items-start gap-2">
            <Hourglass className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <b>{pendingAck.length} asset(s) await your confirmation.</b> Please acknowledge if you have received them, or click &quot;Not received&quot; so IT can investigate.
            </div>
          </div>
        </div>
      )}

      {/* Termination block warning */}
      {terminated && outstanding.length > 0 && (
        <div className="mt-4 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800" data-testid="assets-outstanding-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>{outstanding.length} asset(s) still pending return.</b> Offboarding cannot be marked complete until these are returned or written off.
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">No company assets assigned.</div>
        )}
        {rows.map(a => {
          const ca = a.current_assignment || {};
          const ackStatus = ca.acknowledgement_status;
          const ack = ACK_STYLES[ackStatus];
          const showAckButtons = isSelf && a.status === 'assigned' && ackStatus === 'pending';
          return (
            <div key={a.id} className="border border-slate-200 rounded-xl hover:bg-slate-50" data-testid={`emp-asset-row-${a.id}`}>
              <div className="flex items-center gap-3 p-3">
                <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500">
                  <Laptop className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{a.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    <span className="font-mono">{a.asset_id}</span> · {a.category}
                    {a.serial_number && <> · SN {a.serial_number}</>}
                    {ca.until_offboarding && <> · <span className="text-slate-700"><InfinityIcon className="w-3 h-3 inline" /> until offboarding</span></>}
                    {!ca.until_offboarding && ca.expected_return_date && <> · return by {ca.expected_return_date}</>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[a.status] || ''}`}>
                  {a.status}
                </span>
                {ack && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${ack.cls}`} data-testid={`ack-badge-${a.id}`}>
                    <ack.Icon className="w-3 h-3" /> {ackStatus}
                  </span>
                )}
                {canManage && a.status === 'assigned' && (
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => setReturnFor(a)} data-testid={`emp-return-${a.id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Return
                  </Button>
                )}
              </div>
              {showAckButtons && (
                <div className="px-3 pb-3 -mt-1 flex gap-2 border-t border-slate-100 pt-2.5">
                  <Button size="sm" className="rounded-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => onAcknowledge(a)} data-testid={`acknowledge-${a.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Acknowledge receipt
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setRejectFor(a)} data-testid={`reject-receipt-${a.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Not received
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ReturnDialog asset={returnFor} onClose={() => setReturnFor(null)}
                    onDone={() => { setReturnFor(null); setBump(b => b + 1); }} />
      <AssignByIdDialog key={`assign-${assignOpen}`} open={assignOpen} onClose={() => setAssignOpen(false)}
                        employeeId={employeeId} employeeName={employeeName}
                        onDone={() => { setAssignOpen(false); setBump(b => b + 1); }} />
      <RejectReceiptDialog asset={rejectFor} onClose={() => setRejectFor(null)}
                           onDone={() => { setRejectFor(null); setBump(b => b + 1); }} />
    </div>
  );
}

/* ---------- Assign by Asset ID (employee-profile entry-point) ---------- */
function AssignByIdDialog({ open, onClose, employeeId, employeeName, onDone }) {
  const [assetIdInput, setAssetIdInput] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState('');
  const [untilOffboarding, setUntilOffboarding] = useState(true);
  const [returnDate, setReturnDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const doLookup = async () => {
    if (!assetIdInput.trim()) return;
    setLookupBusy(true); setError(''); setAsset(null);
    try {
      const { data } = await api.get(`/assets/lookup/${encodeURIComponent(assetIdInput.trim())}`);
      if (data.status !== 'available' && data.status !== 'repair') {
        setError(`This asset is currently '${data.status}'. Only available assets can be assigned.`);
        setAsset(data);
      } else {
        setAsset(data);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Asset not found');
    } finally {
      setLookupBusy(false);
    }
  };

  const onConfirm = async () => {
    if (!asset || (asset.status !== 'available' && asset.status !== 'repair')) return;
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/assign`, {
        employee_id: employeeId,
        until_offboarding: untilOffboarding,
        expected_return_date: untilOffboarding ? null : (returnDate || null),
        notes,
      });
      toast.success(`Assigned ${asset.asset_id} to ${employeeName}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-by-id-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Assign asset to {employeeName}</DialogTitle>
          <DialogDescription>
            Enter the Asset ID (e.g. <span className="font-mono">LAN-AST-0001</span>) to look it up, then confirm the assignment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Asset ID *</label>
            <div className="flex gap-2 mt-1">
              <Input value={assetIdInput} onChange={(e) => setAssetIdInput(e.target.value.toUpperCase())}
                     onKeyDown={(e) => e.key === 'Enter' && doLookup()}
                     placeholder="LAN-AST-0001"
                     className="rounded-xl font-mono" data-testid="assign-asset-id-input" />
              <Button onClick={doLookup} disabled={lookupBusy || !assetIdInput.trim()} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="lookup-asset-btn">
                {lookupBusy ? '…' : <><Search className="w-4 h-4 mr-1" /> Find</>}
              </Button>
            </div>
            {error && <div className="text-xs text-rose-600 mt-1" data-testid="lookup-error">{error}</div>}
          </div>

          {asset && (
            <div className="border border-slate-200 rounded-xl p-3 bg-slate-50" data-testid="asset-preview-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
                  <Laptop className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{asset.name}</div>
                  <div className="text-xs text-slate-500">
                    <span className="font-mono">{asset.asset_id}</span> · {asset.category}
                    {asset.brand && <> · {asset.brand}</>}{asset.model && <> {asset.model}</>}
                    {asset.serial_number && <> · SN {asset.serial_number}</>}
                  </div>
                  {asset.assigned_to_name && (
                    <div className="text-xs text-rose-700 mt-1">Currently with {asset.assigned_to_name}</div>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLES[asset.status] || ''}`}>
                  {asset.status}
                </span>
              </div>
            </div>
          )}

          {asset && (asset.status === 'available' || asset.status === 'repair') && (
            <>
              <div className="border border-slate-200 rounded-xl p-3" data-testid="return-policy-block">
                <div className="text-xs font-semibold text-slate-600 mb-2">Return policy</div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="radio" checked={untilOffboarding} onChange={() => setUntilOffboarding(true)} data-testid="policy-until-offboarding" />
                  <InfinityIcon className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm">Until employee resigns or is terminated</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!untilOffboarding} onChange={() => setUntilOffboarding(false)} data-testid="policy-fixed-date" />
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm">Fixed return date</span>
                </label>
                {!untilOffboarding && (
                  <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                         className="rounded-xl mt-2" data-testid="fixed-return-date-input" />
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Notes (optional)</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-xl mt-1" data-testid="assign-notes" />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={onConfirm}
                  disabled={busy || !asset || (asset.status !== 'available' && asset.status !== 'repair') || (!untilOffboarding && !returnDate)}
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]"
                  data-testid="confirm-assign-by-id">
            {busy ? 'Assigning…' : 'Confirm assignment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Return ---------- */
function ReturnDialog({ asset, onClose, onDone }) {
  const [form, setForm] = useState({ return_status: 'returned', notes: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/return`, form);
      toast.success('Return recorded');
      setForm({ return_status: 'returned', notes: '' });
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Return failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="emp-return-asset-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Return {asset?.name}</DialogTitle>
          <DialogDescription>{asset?.asset_id} · {asset?.category}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Return status *</label>
            <Select value={form.return_status} onValueChange={(v) => setForm({ ...form, return_status: v })}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="emp-return-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="returned">Returned (back to available)</SelectItem>
                <SelectItem value="missing">Missing — write off as lost</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="replacement_required">Damaged — replacement required</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Notes</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" data-testid="emp-confirm-return-asset">
            {busy ? 'Saving…' : 'Record return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Reject receipt (employee says "not received") ---------- */
function RejectReceiptDialog({ asset, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/reject-receipt`, { reason });
      toast.success('Reported as not received — IT admin notified');
      setReason('');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!asset} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reject-receipt-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Report &quot;Not received&quot;</DialogTitle>
          <DialogDescription>
            {asset?.asset_id} · {asset?.name}. This will remove the asset from your account and notify IT admin to investigate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="text-xs font-semibold text-slate-600">Reason / context (optional)</label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Box was empty, courier delivered to wrong address…"
                    className="rounded-xl" data-testid="reject-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject-receipt">
            {busy ? 'Submitting…' : 'Submit — Not received'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
